// features/auth/services/auth.service.ts
import BaseApiService from '@/services/base-api.service';
import { API_BASE_URL } from '@/config/api';
import { 
  LoginCredentials, 
  RegisterCredentials, 
  AuthResponse,
  PasswordResetRequest,
  PasswordResetConfirm,
  User
} from '@/features/auth/types/auth.types';

class AuthService extends BaseApiService {
  constructor() {
    super(API_BASE_URL);
  }

  async login(credentials: LoginCredentials): Promise<{ auth: AuthResponse; user: User }> {
    try {
      // Create form data
      const formData = new URLSearchParams();
      formData.append('grant_type', 'password');
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);
      formData.append('scope', '');
      formData.append('client_id', 'string');
      formData.append('client_secret', 'string');

      // Log the request data for debugging
      console.log('Login request data:', {
        url: '/auth/login',
        data: formData.toString(),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        }
      });

      const response = await this.post<AuthResponse>('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        }
      });
      
      // Store tokens
      this.storeAuthData(response.data);

      // Fetch and store user data
      const user = await this.fetchAndStoreUserData();

      return {
        auth: response.data,
        user
      };
    } catch (error) {
      console.error('Login failed', error);
      throw error;
    }
  }

  async register(credentials: RegisterCredentials): Promise<{ auth: AuthResponse; user: User }> {
    try {
      const response = await this.post<AuthResponse>('/auth/signup', credentials, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Store tokens
      this.storeAuthData(response.data);

      // Fetch and store user data
      const user = await this.fetchAndStoreUserData();

      return {
        auth: response.data,
        user
      };
    } catch (error) {
      console.error('Registration failed', error);
      throw error;
    }
  }

  async logout() {
    try {
      // Optional: Invalidate token on backend
      await this.post('/auth/logout');
    } catch (error) {
      console.error('Logout failed', error);
    } finally {
      this.clearAuthData();
    }
  }

  async requestPasswordReset(request: PasswordResetRequest) {
    try {
      const response = await this.post('/auth/password-reset-request', request);
      return response.data;
    } catch (error) {
      console.error('Password reset request failed', error);
      throw error;
    }
  }

  async confirmPasswordReset(request: PasswordResetConfirm) {
    try {
      const response = await this.post('/auth/password-reset-confirm', request);
      return response.data;
    } catch (error) {
      console.error('Password reset confirmation failed', error);
      throw error;
    }
  }

  // Helper method to store authentication data
  private storeAuthData(authData: AuthResponse) {
    if (authData.access_token) {
      localStorage.setItem('access_token', authData.access_token);
    }
    if (authData.refresh_token) {
      localStorage.setItem('refresh_token', authData.refresh_token);
    }
  }

  // Helper method to clear authentication data
  private clearAuthData() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  // Get current user from API
  async getCurrentUser(): Promise<User> {
    try {
      const response = await this.get<User>('/users/me');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      throw error;
    }
  }

  // Helper method to fetch and store user data
  async fetchAndStoreUserData(): Promise<User> {
    const user = await this.getCurrentUser();
    localStorage.setItem('user', JSON.stringify(user));
    return user;
  }
}

export default new AuthService();